import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { cn } from "@/lib/utils";

/**
 * VP-208 — reużywalne kompozycje loading skeletonów.
 * Wymiary odwzorowują realne layouty (MetricCard, wykresy, tabele, listy),
 * żeby po załadowaniu danych nie było skoków layoutu.
 */

/**
 * Kafelek KPI — odwzorowuje `DashboardMetricCard` (nagłówek z ikoną po prawej,
 * wartość, linia trendu), żeby siatka KPI nie zmieniła wysokości po załadowaniu.
 */
function KpiTileSkeleton({ className }: { className?: string }) {
  return (
    <Card className={cn("h-full bg-card border-border", className)}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 px-6 pt-6">
        <Skeleton className="h-3 w-24" />
        <Skeleton className="h-4 w-4 rounded shrink-0" />
      </CardHeader>
      <CardContent className="px-6 pb-6 pt-0">
        <Skeleton className="h-8 w-28 mb-3" />
        <Skeleton className="h-3 w-16" />
      </CardContent>
    </Card>
  );
}

/** Siatka kafelków KPI — className przekazuje realny układ gridu danego widoku. */
function KpiGridSkeleton({
  count = 4,
  className = "grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4",
}: {
  count?: number;
  className?: string;
}) {
  return (
    <div className={className}>
      {Array.from({ length: count }).map((_, i) => (
        <KpiTileSkeleton key={i} />
      ))}
    </div>
  );
}

/**
 * Karta wykresu — nagłówek z pigułkami metryk + obszar wykresu.
 * Domyślne proporcje 3.5/1 odpowiadają `aspectRatio` realnego VisxAreaChart,
 * `min-h-[16rem]` pilnuje wysokości pustego wykresu na wąskich ekranach.
 */
function ChartSkeleton({
  bodyClassName = "aspect-[3.5/1] min-h-[16rem]",
  className,
}: {
  bodyClassName?: string;
  className?: string;
}) {
  return (
    <Card className={cn("bg-muted/50 border-border rounded-xl overflow-hidden", className)}>
      <div className="p-4 md:p-5 border-b border-border">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <Skeleton className="h-5 w-36" />
          <div className="flex flex-wrap gap-1.5">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-6 w-16 rounded-md" />
            ))}
          </div>
        </div>
      </div>
      <CardContent className="p-2 md:p-3">
        <Skeleton className={cn("w-full rounded-md", bodyClassName)} />
      </CardContent>
    </Card>
  );
}

/** Tabela — nagłówek + wiersze; szerokości kolumn lekko zróżnicowane, bez migotania. */
function TableSkeleton({
  rows = 5,
  columns = 5,
  className,
}: {
  rows?: number;
  columns?: number;
  className?: string;
}) {
  const widths = ["w-3/4", "w-1/2", "w-2/3", "w-1/3", "w-3/5"];
  return (
    <div className={cn("w-full space-y-2", className)}>
      <div className="flex gap-4 pb-2 border-b border-border">
        {Array.from({ length: columns }).map((_, i) => (
          <Skeleton key={i} className="h-3.5 flex-1" />
        ))}
      </div>
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex gap-4 py-1.5">
          {Array.from({ length: columns }).map((_, c) => (
            <div key={c} className="flex-1">
              <Skeleton className={cn("h-4", widths[(r + c) % widths.length])} />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

/** Wiersz tabeli (np. drill-down ad-setów/adów) do wpięcia w istniejący <tbody>. */
function TableRowSkeleton({ columns = 5, colSpan }: { columns?: number; colSpan?: number }) {
  if (colSpan != null) {
    return (
      <tr>
        <td colSpan={colSpan} className="px-4 py-3">
          <Skeleton className="h-4 w-full" />
        </td>
      </tr>
    );
  }
  return (
    <tr>
      {Array.from({ length: columns }).map((_, i) => (
        <td key={i} className="px-4 py-3">
          <Skeleton className="h-4 w-3/4" />
        </td>
      ))}
    </tr>
  );
}

/** Lista pozycji (leady, rozmowy, pliki): awatar + dwie linie tekstu na wiersz. */
function ListSkeleton({
  rows = 5,
  withAvatar = true,
  className,
}: {
  rows?: number;
  withAvatar?: boolean;
  className?: string;
}) {
  return (
    <div className={cn("space-y-3", className)}>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 rounded-lg border border-border p-3">
          {withAvatar && <Skeleton className="w-9 h-9 rounded-full shrink-0" />}
          <div className="flex-1 min-w-0 space-y-2">
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="h-3 w-1/2" />
          </div>
          <Skeleton className="h-4 w-16 shrink-0" />
        </div>
      ))}
    </div>
  );
}

/** Blok tekstowy/formularzowy — generyczny placeholder o zadanej wysokości. */
function BlockSkeleton({ className = "h-24 w-full" }: { className?: string }) {
  return <Skeleton className={cn("rounded-md", className)} />;
}

/**
 * Siatka kafelków z mediami (biblioteka grafik, pickery avatarów/produktów).
 * Odwzorowuje kartę z obramowaniem: obszar miniatury + stopka na podpis/tagi —
 * czyli kształt `GraphicCard`, żeby siatka nie zmieniła wysokości po załadowaniu.
 * `aspect` dopasuj do miniatur widoku (1:1 grafiki, 3/4 portret, 9/16 stories),
 * a `gridClassName` musi być tą samą siatką, której widok używa dla danych.
 */
function MediaGridSkeleton({
  count = 12,
  aspect = "aspect-square",
  withFooter = true,
  gridClassName = "grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3",
}: {
  count?: number;
  aspect?: string;
  withFooter?: boolean;
  gridClassName?: string;
}) {
  return (
    <div className={gridClassName}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="rounded-xl overflow-hidden border border-border bg-muted/50">
          <Skeleton className={cn("w-full rounded-none", aspect)} />
          {withFooter && (
            // p-2.5 / space-y-2 — te same odstępy co stopka `GraphicCard`.
            <div className="p-2.5 space-y-2">
              <Skeleton className="h-3.5 w-2/3" />
              <Skeleton className="h-3.5 w-1/2" />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * Cała strona dashboardowa: siatka KPI + wykres — pierwszy paint bez spinnera.
 * Domyślny `gridClassName` to siatka głównych KPI dashboardu; inne widoki
 * (np. Allegro) podają własną, żeby liczba kolumn nie zmieniła się po załadowaniu.
 */
function DashboardSkeleton({
  kpiCount = 6,
  gridClassName = "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-dense md:gap-balanced",
}: {
  kpiCount?: number;
  gridClassName?: string;
}) {
  return (
    <div className="space-y-4 md:space-y-6">
      <KpiGridSkeleton count={kpiCount} className={gridClassName} />
      <ChartSkeleton />
    </div>
  );
}

export {
  KpiTileSkeleton,
  KpiGridSkeleton,
  ChartSkeleton,
  TableSkeleton,
  TableRowSkeleton,
  ListSkeleton,
  BlockSkeleton,
  MediaGridSkeleton,
  DashboardSkeleton,
};
